import React, { useEffect, useRef, useMemo } from 'react';
import Chart from 'chart.js/auto';
import './PayoffDiagram.css';

const DEFAULT_X_MIN = 0;
const DEFAULT_X_MAX = 200;
const X_MARGIN = 20;

const LINE_COLORS = [
  '#e6194b', '#3cb44b', '#4363d8', '#f58231', '#911eb4',
  '#42d4f4', '#f032e6', '#bfef45', '#fabed4', '#469990'
];

function computeAxisRange(securities) {
  const prices = [];
  securities.forEach(s => {
    if (s.type === 'Call' || s.type === 'Put') prices.push(s.strikePrice);
    if (s.type === 'Stock' && s.purchasePrice) prices.push(s.purchasePrice);
  });
  if (prices.length === 0) return { xMin: DEFAULT_X_MIN, xMax: DEFAULT_X_MAX };
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const range = Math.max(max - min, 30);
  return {
    xMin: Math.max(0, Math.floor(min - range * 0.5 - X_MARGIN)),
    xMax: Math.ceil(max + range * 0.5 + X_MARGIN)
  };
}

function calculatePayoff(security, stockPrice) {
  const dir = security.position === 'short' ? -1 : 1;
  switch (security.type) {
    case 'Stock':
      return security.quantity * dir * (stockPrice - (security.purchasePrice || 0));
    case 'Bond':
      return security.quantity * dir * (security.faceValue || 0);
    case 'Call': {
      const intrinsic = Math.max(0, stockPrice - security.strikePrice);
      return security.quantity * dir * intrinsic;
    }
    case 'Put': {
      const intrinsic = Math.max(0, security.strikePrice - stockPrice);
      return security.quantity * dir * intrinsic;
    }
    default:
      return 0;
  }
}

function calculateNetPremium(securities) {
  return securities.reduce((sum, s) => {
    if (s.type !== 'Call' && s.type !== 'Put') return sum;
    const prem = (s.quantity || 0) * (Number(s.premium) || 0);
    return sum + (s.position === 'long' ? prem : -prem);
  }, 0);
}

function findBreakevens(data) {
  const out = [];
  for (let i = 0; i < data.length - 1; i++) {
    const a = data[i], b = data[i + 1];
    if (a.y * b.y <= 0 && a.y !== b.y) {
      const t = -a.y / (b.y - a.y);
      out.push(Math.round((a.x + t * (b.x - a.x)) * 100) / 100);
    }
  }
  return out;
}

function getSecurityLabel(security) {
  const pos = security.position === 'long' ? 'Long' : 'Short';
  const qty = security.quantity !== 1 ? ` \u00d7${security.quantity}` : '';
  if (security.type === 'Call' || security.type === 'Put') {
    return `${pos} ${security.type} K=${security.strikePrice}${qty}`;
  }
  return `${pos} ${security.type}${qty}`;
}

function PayoffDiagram({ securities, showIndividual, showNetPnL = true, showPayoffTable = false, xMin: userXMin, xMax: userXMax, darkMode = false }) {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  const { xMin: autoMin, xMax: autoMax } = computeAxisRange(securities);
  const xMin = userXMin != null && userXMin !== '' ? Number(userXMin) : autoMin;
  const xMax = userXMax != null && userXMax !== '' ? Number(userXMax) : autoMax;

  const stockPrices = useMemo(() => {
    const s = Math.max(0.5, (xMax - xMin) / 200);
    const prices = [];
    for (let p = xMin; p <= xMax; p += s) prices.push(Math.round(p * 100) / 100);
    return prices;
  }, [xMin, xMax]);

  const netPremium = calculateNetPremium(securities);

  const portfolioData = useMemo(() =>
    stockPrices.map(price => ({
      x: price,
      y: securities.reduce((sum, sec) => sum + calculatePayoff(sec, price), 0)
    })),
    [stockPrices, securities]
  );

  const netPnLData = useMemo(() => {
    if (!showNetPnL || netPremium === 0) return null;
    return portfolioData.map(pt => ({ x: pt.x, y: pt.y - netPremium }));
  }, [portfolioData, showNetPnL, netPremium]);

  const breakevens = useMemo(() => findBreakevens(portfolioData), [portfolioData]);
  const netBreakevens = useMemo(() => netPnLData ? findBreakevens(netPnLData) : [], [netPnLData]);

  const stats = useMemo(() => {
    if (securities.length === 0) return null;
    const source = netPnLData || portfolioData;
    const ys = source.map(d => d.y);
    const maxProfit = Math.max(...ys);
    const maxLoss = Math.min(...ys);
    const be = netPnLData ? netBreakevens : breakevens;
    return { maxProfit, maxLoss, breakevens: be };
  }, [securities.length, portfolioData, netPnLData, breakevens, netBreakevens]);

  const tableData = useMemo(() => {
    if (!showPayoffTable || securities.length === 0) return [];
    const keyPrices = new Set();
    securities.forEach(s => {
      if (s.type === 'Call' || s.type === 'Put') keyPrices.add(s.strikePrice);
      if (s.type === 'Stock' && s.purchasePrice) keyPrices.add(s.purchasePrice);
    });
    const allBE = netPnLData ? netBreakevens : breakevens;
    allBE.forEach(be => keyPrices.add(be));
    keyPrices.add(xMin);
    keyPrices.add(xMax);
    const mid = Math.round((xMin + xMax) / 2);
    keyPrices.add(mid);
    const q1 = Math.round((xMin + mid) / 2);
    const q3 = Math.round((mid + xMax) / 2);
    keyPrices.add(q1);
    keyPrices.add(q3);

    const sorted = [...keyPrices].filter(p => p >= xMin && p <= xMax).sort((a, b) => a - b);
    return sorted.map(price => {
      const total = securities.reduce((sum, sec) => sum + calculatePayoff(sec, price), 0);
      const net = total - netPremium;
      const individual = securities.map(sec => calculatePayoff(sec, price));
      return { price, total, net, individual };
    });
  }, [showPayoffTable, securities, xMin, xMax, breakevens, netBreakevens, netPnLData, netPremium]);

  // Crosshair plugin
  const crosshairPlugin = useRef({
    id: 'crosshair',
    afterDraw(chart) {
      if (chart._active && chart._active.length > 0) {
        const x = chart._active[0].element.x;
        const yScale = chart.scales.y;
        const ctx = chart.ctx;
        ctx.save();
        ctx.setLineDash([3, 3]);
        ctx.strokeStyle = chart.data._darkMode ? 'rgba(200,200,200,0.4)' : 'rgba(100,100,100,0.4)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x, yScale.top);
        ctx.lineTo(x, yScale.bottom);
        ctx.stroke();
        ctx.restore();
      }
    }
  });

  // Breakeven lines plugin
  const breakevenPlugin = useRef({
    id: 'breakevenLines',
    afterDraw(chart) {
      const bkevens = chart.data._breakevens || [];
      if (bkevens.length === 0) return;
      const xScale = chart.scales.x;
      const yScale = chart.scales.y;
      const ctx = chart.ctx;
      const isDark = chart.data._darkMode;
      ctx.save();
      ctx.setLineDash([4, 4]);
      ctx.strokeStyle = isDark ? 'rgba(200,200,200,0.5)' : 'rgba(100,100,100,0.7)';
      ctx.lineWidth = 1;
      bkevens.forEach(xVal => {
        const xPx = xScale.getPixelForValue(xVal);
        ctx.beginPath();
        ctx.moveTo(xPx, yScale.top);
        ctx.lineTo(xPx, yScale.bottom);
        ctx.stroke();
      });
      ctx.setLineDash([]);
      ctx.fillStyle = isDark ? '#adb5bd' : '#495057';
      ctx.font = '11px system-ui, sans-serif';
      ctx.textAlign = 'center';
      const zeroY = yScale.getPixelForValue(0);
      bkevens.forEach(xVal => {
        const xPx = xScale.getPixelForValue(xVal);
        ctx.fillText(`BE: $${xVal}`, xPx, zeroY + (zeroY < yScale.bottom - 14 ? 14 : -6));
      });
      ctx.restore();
    }
  });

  useEffect(() => {
    if (!chartRef.current) return;

    const textColor = darkMode ? '#c0c0c0' : '#666';
    const gridColor = darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
    const zeroLineColor = darkMode ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.2)';

    const datasets = [];

    if (showIndividual) {
      securities.forEach((security, index) => {
        datasets.push({
          label: security.label || getSecurityLabel(security),
          data: stockPrices.map(price => ({
            x: price,
            y: calculatePayoff(security, price)
          })),
          borderColor: LINE_COLORS[index % LINE_COLORS.length],
          borderWidth: 1.5,
          fill: false,
          tension: 0,
          pointRadius: 0,
          pointHitRadius: 8
        });
      });
    }

    datasets.push({
      label: 'Portfolio Payoff',
      data: portfolioData,
      borderColor: darkMode ? '#ffffff' : '#000000',
      borderWidth: 2.5,
      fill: {
        target: 'origin',
        above: 'rgba(40, 167, 69, 0.12)',
        below: 'rgba(220, 53, 69, 0.12)'
      },
      tension: 0,
      pointRadius: 0,
      pointHitRadius: 8
    });

    if (netPnLData) {
      datasets.push({
        label: 'Net P&L (after premium)',
        data: netPnLData,
        borderColor: '#3b82f6',
        borderWidth: 2,
        borderDash: [6, 3],
        fill: false,
        tension: 0,
        pointRadius: 0,
        pointHitRadius: 8
      });
    }

    const allBreakevens = [...new Set([...breakevens, ...netBreakevens])];

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 150 },
      scales: {
        x: {
          type: 'linear',
          position: 'center',
          title: { display: true, text: 'Stock Price ($)', color: textColor, font: { size: 13 } },
          min: xMin,
          max: xMax,
          grid: { color: gridColor },
          ticks: { color: textColor },
          border: { color: zeroLineColor, width: 1.5 }
        },
        y: {
          type: 'linear',
          position: 'left',
          title: { display: true, text: 'Payoff ($)', color: textColor, font: { size: 13 } },
          grid: { color: gridColor },
          ticks: { color: textColor },
          border: { color: zeroLineColor },
          beginAtZero: false,
          grace: '10%'
        }
      },
      plugins: {
        tooltip: {
          mode: 'index',
          intersect: false,
          backgroundColor: darkMode ? '#2d313a' : '#fff',
          titleColor: darkMode ? '#e0e0e0' : '#333',
          bodyColor: darkMode ? '#c0c0c0' : '#555',
          borderColor: darkMode ? '#555' : '#ddd',
          borderWidth: 1,
          padding: 10,
          callbacks: {
            title: (items) => items[0] ? `Stock Price: $${items[0].parsed.x.toFixed(2)}` : '',
            label: (ctx) => `  ${ctx.dataset.label}: $${ctx.parsed.y.toFixed(2)}`
          }
        },
        legend: {
          position: 'top',
          labels: {
            color: textColor,
            usePointStyle: true,
            pointStyle: 'line',
            padding: 16,
            font: { size: 12 }
          }
        }
      },
      interaction: { mode: 'index', axis: 'x', intersect: false }
    };

    if (!chartInstance.current) {
      const ctx = chartRef.current.getContext('2d');
      chartInstance.current = new Chart(ctx, {
        type: 'line',
        data: { datasets, _breakevens: allBreakevens, _darkMode: darkMode },
        plugins: [breakevenPlugin.current, crosshairPlugin.current],
        options: chartOptions
      });
    } else {
      const chart = chartInstance.current;
      chart.data.datasets = datasets;
      chart.data._breakevens = allBreakevens;
      chart.data._darkMode = darkMode;
      Object.assign(chart.options, chartOptions);
      chart.update('none');
    }
  }, [securities, showIndividual, showNetPnL, xMin, xMax, darkMode, stockPrices, portfolioData, netPnLData, breakevens, netBreakevens]);

  useEffect(() => {
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, []);

  const handleExportImage = () => {
    if (!chartRef.current) return;
    const link = document.createElement('a');
    link.download = 'payoff-diagram.png';
    link.href = chartRef.current.toDataURL('image/png');
    link.click();
  };

  const fmt = (v) => {
    if (v >= 1e12) return 'Unlimited';
    if (v <= -1e12) return 'Unlimited';
    return `$${v.toFixed(2)}`;
  };

  return (
    <div className="payoff-diagram-wrapper">
      <div className="payoff-diagram" role="img" aria-label="Payoff diagram">
        <canvas ref={chartRef}></canvas>
      </div>

      {stats && securities.length > 0 && (
        <div className="summary-stats">
          <div className="stat">
            <span className="stat-label">Max Profit</span>
            <span className="stat-value stat-profit">{fmt(stats.maxProfit)}</span>
          </div>
          <div className="stat">
            <span className="stat-label">Max Loss</span>
            <span className="stat-value stat-loss">{fmt(stats.maxLoss)}</span>
          </div>
          {stats.breakevens.length > 0 && (
            <div className="stat">
              <span className="stat-label">Breakeven{stats.breakevens.length > 1 ? 's' : ''}</span>
              <span className="stat-value">{stats.breakevens.map(b => `$${b}`).join(', ')}</span>
            </div>
          )}
          {netPremium !== 0 && (
            <div className="stat">
              <span className="stat-label">Net Premium</span>
              <span className={`stat-value ${netPremium > 0 ? 'stat-loss' : 'stat-profit'}`}>
                {netPremium > 0 ? `\u2212$${netPremium.toFixed(2)} (debit)` : `+$${Math.abs(netPremium).toFixed(2)} (credit)`}
              </span>
            </div>
          )}
          <button type="button" className="export-btn" onClick={handleExportImage} title="Download as PNG">
            Export PNG
          </button>
        </div>
      )}

      {showPayoffTable && tableData.length > 0 && (
        <div className="payoff-table-wrapper">
          <table className="payoff-table">
            <thead>
              <tr>
                <th>Stock Price</th>
                {securities.map((s) => (
                  <th key={s.id}>{s.label || getSecurityLabel(s)}</th>
                ))}
                <th>Total</th>
                {netPremium !== 0 && <th>Net P&L</th>}
              </tr>
            </thead>
            <tbody>
              {tableData.map((row, i) => (
                <tr key={i}>
                  <td>${row.price.toFixed(2)}</td>
                  {row.individual.map((v, j) => (
                    <td key={j} className={v > 0.005 ? 'cell-profit' : v < -0.005 ? 'cell-loss' : ''}>
                      ${v.toFixed(2)}
                    </td>
                  ))}
                  <td className={row.total > 0.005 ? 'cell-profit' : row.total < -0.005 ? 'cell-loss' : ''}>
                    <strong>${row.total.toFixed(2)}</strong>
                  </td>
                  {netPremium !== 0 && (
                    <td className={row.net > 0.005 ? 'cell-profit' : row.net < -0.005 ? 'cell-loss' : ''}>
                      <strong>${row.net.toFixed(2)}</strong>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default PayoffDiagram;
